import { Controller } from "@hotwired/stimulus"

/**
 * Pilot Report Progress Controller
 *
 * Subscribes to PilotReportChannel via ActionCable to display
 * real-time progress during report generation.
 *
 * Works for both "pending" and "generating" initial states:
 * - pending: shows pending banner, switches to progress bar on first broadcast
 * - generating: shows progress bar immediately
 *
 * Flow:
 * 1. Connect -> Subscribe to channel with report ID
 * 2. On progress -> Hide pending banner, show progress bar, update percentage
 * 3. On completion -> Reload page via Turbo to show ready state
 * 4. On error -> Hide progress, show error banner
 */
export default class extends Controller {
  static targets = [
    "pendingBanner",
    "progressSection",
    "progressBar",
    "stepLabel",
    "percentageText",
    "errorContainer",
    "errorMessage"
  ]

  static values = {
    reportId: Number,
    initialStatus: { type: String, default: "generating" }
  }

  connect() {
    if (!this.reportIdValue) return

    this.subscribe()
  }

  disconnect() {
    this.unsubscribe()
  }

  subscribe() {
    const consumer = window._cableConsumer
    if (!consumer) {
      console.warn("[PilotReportProgress] No ActionCable consumer found")
      return
    }

    this.subscription = consumer.subscriptions.create(
      { channel: "PilotReportChannel", report_id: this.reportIdValue },
      {
        received: (data) => this.handleReceived(data),
        connected: () => console.log("[PilotReportProgress] Connected"),
        disconnected: () => console.log("[PilotReportProgress] Disconnected")
      }
    )
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleReceived(data) {
    switch (data.type) {
      case "progress":
        this.handleProgress(data)
        break
      case "completion":
        this.handleCompletion(data)
        break
      case "error":
        this.handleError(data)
        break
    }
  }

  showProgressSection() {
    if (this.hasPendingBannerTarget) {
      this.pendingBannerTarget.classList.add("hidden")
    }
    if (this.hasProgressSectionTarget) {
      this.progressSectionTarget.classList.remove("hidden")
    }
  }

  handleProgress(data) {
    this.showProgressSection()

    const percent = data.percentage || 0

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }

    if (this.hasPercentageTextTarget) {
      this.percentageTextTarget.textContent = `${percent}%`
    }

    if (this.hasStepLabelTarget && data.step) {
      const stepText = this.stepTranslation(data.step)
      this.stepLabelTarget.textContent = stepText
    }
  }

  handleCompletion(_data) {
    this.showProgressSection()

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = "100%"
    }

    if (this.hasPercentageTextTarget) {
      this.percentageTextTarget.textContent = "100%"
    }

    if (this.hasStepLabelTarget) {
      this.stepLabelTarget.textContent = this.stepTranslation("complete")
    }

    // Reload via Turbo to show the ready state with download button
    setTimeout(() => {
      window.Turbo.visit(window.location.href, { action: "replace" })
    }, 500)
  }

  handleError(data) {
    if (this.hasPendingBannerTarget) {
      this.pendingBannerTarget.classList.add("hidden")
    }
    if (this.hasProgressSectionTarget) {
      this.progressSectionTarget.classList.add("hidden")
    }

    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.remove("hidden")
    }

    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = data.error_message || "An error occurred"
    }
  }

  stepTranslation(step) {
    const translations = {
      initializing: this.element.dataset.stepInitializing || "Initializing...",
      collecting_data: this.element.dataset.stepCollectingData || "Collecting data...",
      generating_file: this.element.dataset.stepGeneratingFile || "Generating file...",
      saving_file: this.element.dataset.stepSavingFile || "Saving file...",
      complete: this.element.dataset.stepComplete || "Complete!"
    }
    return translations[step] || step
  }
}
